import { Button, Table, Checkbox, Input, Select } from "antd";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import apiClient from "../../../utils/apiClient";
import PropTypes from "prop-types";
import useToast from "../../../hooks/userToast";
import useUser from "../../../hooks/useUser";
import { Option } from "antd/es/mentions";

RadioPreferenceModal.propTypes = {
  studyID: PropTypes.string,
  studyPrimaryKey: PropTypes.number,
  handleOk: PropTypes.func,
  isEmergency: PropTypes.number,
  fetchData: PropTypes.func,
};

function RadioPreferenceModal({
  studyID,
  studyPrimaryKey,
  handleOk,
  fetchData,
  isEmergency,
}) {
  const [selectedRadiologist, setSelectedRadiologist] = useState(null);
  const [selectedRadiologistID, setSelectedRadiologistID] = useState();
  const [selectedSlotID, setSelectedSlotID] = useState();
  const [allRadiologists, setAllRadiologists] = useState([]);
  const [radiologistsData, setRadiologistsData] = useState([]);
  const [isConfirmEmergency, setConfirmEmergency] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(false);

  const { toastError } = useToast();
  const tableRef = useRef(null);
  const { userID } = useUser();

  const fetchAllPreferences = async () => {
    try {
      const response = await apiClient.get(
        `availability/getAssignRadiologist/${studyID}`
      );
      const fetchedData = response?.data?.Availability || [];

      // Extract names of all radiologists regardless of availability
      const allRadios = fetchedData.map((radiologist) => {
        const availableSlots = radiologist.availabilies;
        return {
          name: radiologist.name,
          availabilityCount: availableSlots.length,
        };
      });

      setAllRadiologists(allRadios);
    } catch (error) {
      console.error(error);
    }
  };

  const flattenRadiologistAvailabilityData = (radiologistAvailabilityList) => {
    return radiologistAvailabilityList.flatMap((radiologistData) => {
      if (radiologistData.availability.length > 0) {
        return radiologistData.availability.map((availability) => ({
          key: `${radiologistData.radiologist.id}-${availability.id}`,
          radiologist_id: radiologistData.radiologist.id,
          name: radiologistData.radiologist.name,
          age: radiologistData.radiologist.age,
          gender: radiologistData.radiologist.gender,
          start_date: availability.start_date,
          start_time: availability.start_time,
          end_date: availability.end_date,
          end_time: availability.end_time,
          status: availability.status,
          availability_id: availability.id,
          availStatus: availability.status,
        }));
      } else {
        return [
          {
            key: `${radiologistData.radiologist.id}-no-availability`,
            radiologist_id: radiologistData.radiologist.id,
            name: radiologistData.radiologist.name,
            age: radiologistData.radiologist.age,
            gender: radiologistData.radiologist.gender,
            start_date: null,
            start_time: null,
            end_date: null,
            end_time: null,
            status: "no availability",
            availability_id: null,
            availStatus: "no availability",
          },
        ];
      }
    });
  };

  const getAllRadiologists = async () => {
    try {
      const categoryParam = selectedCategory === "all" ? "" : selectedCategory;

      const response = await apiClient.get(
        `/availability/getAllRadiologistAvailability?category=${categoryParam}`
      );
      const fetchedRadiologistsData = response?.data?.radiologistAvailability;
      if (fetchedRadiologistsData) {
        const flattenedData = flattenRadiologistAvailabilityData(
          fetchedRadiologistsData
        );
        setRadiologistsData(flattenedData);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get(`/category`);
      setCategories(response?.data?.category?.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchAllPreferences();
  }, [studyID]);

  useEffect(() => {
    getAllRadiologists();
  }, [selectedCategory]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const formData = {
        study_info_model_id: studyPrimaryKey,
        role_radiologist: selectedRadiologistID,
        status: "assigned",
        role_sub_admin: userID,
        study_flag: isConfirmEmergency && isEmergency ? 1 : 0,
      };
      await apiClient.post(
        "auth/create-study-status",
        !selectedSlotID
          ? formData
          : { ...formData, availability_id: selectedSlotID }
      );
      await handleOk();
      await fetchData();
    } catch (error) {
      toastError(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmEmergency = (checked) => {
    setConfirmEmergency(checked);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
  };

  const filteredRadiologistsData = radiologistsData.filter((radiologist) =>
    radiologist.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const radiologistsColumns = [
    {
      title: "Radiologist Name",
      key: "name",
      width: 200,
      render: (text, record) => (
        <div className="d-flex justify-content-between align-items-center">
          <span>{record.name}</span>
        </div>
      ),
    },
    {
      title: "Available Date",
      width: 200,
      render: (text, record) => {
        if (!record.start_date || !record.end_date) {
          return "No Availability";
        }
        const startDate = moment(record.start_date).format("DD/MM/YY");
        const endDate = moment(record.end_date).format("DD/MM/YY");
        return <>{`${startDate} - ${endDate}`}</>;
      },
    },
    {
      title: "Available Time",
      width: 200,
      render: (text, record) => {
        if (!record.start_time || !record.end_time) {
          return "No Availability";
        }
        const startTime = moment(record.start_time, "HH:mm:ss").format(
          "hh:mm A"
        );
        const endTime = moment(record.end_time, "HH:mm:ss").format("hh:mm A");
        return <>{`${startTime} - ${endTime}`}</>;
      },
    },
    {
      title: "",
      width: 100,
      render: (text, record) => (
        <input
          type="radio"
          style={{ marginLeft: "20px", cursor: "pointer" }}
          checked={selectedRadiologist === record.key}
          onChange={() => {
            setSelectedRadiologist(record.key);
            setSelectedRadiologistID(record.radiologist_id);
            setSelectedSlotID(record?.availability_id);
          }}
        />
      ),
    },
  ];

  return (
    <div ref={tableRef}>
      <div className="d-flex align-items-center my-3 assign_studycase">
        <div
          style={{
            marginLeft: "20px",
            marginBottom: "0px",
            width: "60%",
            display: "flex",
            justifyContent: "space-around",
          }}
        >
          <Input
            type="text"
            placeholder="Search Radiologist by Name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ padding: "8px", marginBottom: "8px" }}
          />

          <Select
            style={{ width: "200px", height: "39px", marginLeft: "20px" }}
            showSearch
            placeholder="Select Category"
            value={selectedCategory || "all"}
            onChange={handleCategoryChange}
          >
            <Option value="all">All</Option>
            {categories?.map((element) => (
              <Option key={element?.id} value={element?.id}>
                {element?.name}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      <div style={{ height: "300px", marginBottom: "0px" }}>
        <Table
          scroll={{ y: 250, x: "max-content" }}
          columns={radiologistsColumns}
          dataSource={filteredRadiologistsData}
          rowKey="key"
          pagination={false}
          size="small"
          className="RadiologistsTable"
          sticky
        />
      </div>

      <div className="d-flex justify-content-end my-2">
        <Button
          className="btn btn-primary"
          type="button"
          onClick={handleSubmit}
          disabled={loading || !selectedRadiologist}
          loading={loading}
        >
          Submit
        </Button>
      </div>

      {isEmergency === 1 && (
        <div
          style={{
            border: "1px solid rgb(217, 217, 217)",
            borderRadius: "8px",
            padding: "5px",
            background: "rgb(249, 249, 249)",
          }}
        >
          <div className="py-2">
            <Checkbox
              checked={isConfirmEmergency}
              onChange={(e) => {
                handleConfirmEmergency(e.target.checked);
              }}
            >
              <span style={{ fontSize: "18px" }}>Send Study As Emergency?</span>
            </Checkbox>
          </div>
        </div>
      )}

      <div
        style={{
          border: "1px solid #d9d9d9",
          borderRadius: "8px",
          padding: "16px",
          backgroundColor: "#f9f9f9",
          marginTop: "20px",
        }}
      >
        <h4 style={{ marginBottom: "16px", color: "#016fb9" }}>
          Radiologist Priority by Center
        </h4>
        {allRadiologists.length > 0 ? (
          <ul style={{ listStyleType: "none", paddingLeft: "0" }}>
            {allRadiologists.map((radiologist, index) => (
              <li
                key={index}
                style={{ padding: "8px 0", borderBottom: "1px solid #e8e8e8" }}
              >
                <span style={{ fontWeight: "bold" }}>{radiologist.name}</span> -{" "}
                {radiologist.availabilityCount > 0 ? (
                  <span style={{ color: "rgb(55 139 14)" }}>
                    Has Availability ({radiologist.availabilityCount} slots)
                  </span>
                ) : (
                  <span style={{ color: "#ff4d4f" }}>No Availability</span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p style={{ color: "#ff4d4f" }}>No radiologists found</p>
        )}
      </div>
    </div>
  );
}

export default RadioPreferenceModal;
